import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

import { AUTH_LOGOUT_EVENT, logout } from "@/services/auth"
import { TokenResponse, Tokens } from "../../interfaces/authentication"
import api, { AuthAPI, getFreshCsrfToken } from "../../services/api"
import { User } from "../../types"
import { useError } from "../ErrorContext"
import { useSuccess } from "../SuccessContext"
import {
    AuthenticationDispatchContext,
    AuthenticationStateContext,
    initialTokens,
} from "./AuthenticationContext.context"

// ── Constants ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = "authentication"

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Decode the `exp` claim of a JWT and return its value in milliseconds. */
function getJwtExpirationMs(token: string): number {
    try {
        const payload = JSON.parse(atob(token.split(".")[1]))
        return (payload.exp as number) * 1000
    } catch {
        console.error("Failed to decode JWT:", token)
        return 0
    }
}

/** Read tokens from localStorage, falling back to initialTokens on any error. */
function loadTokensFromStorage(): Tokens {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
            const parsed = JSON.parse(raw)
            return { ...initialTokens, ...parsed }
        }
    } catch {
        console.error("Failed to parse tokens from localStorage in " + STORAGE_KEY)
        // ignore
    }
    return initialTokens
}

// ── Provider ───────────────────────────────────────────────────────────────────

interface AuthenticationContextProviderProps {
    children: React.ReactNode
}

export const AuthenticationContextProvider: React.FC<AuthenticationContextProviderProps> = ({ children }) => {
    const [tokens, setTokens] = useState<Tokens>(loadTokensFromStorage)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [user, setUser] = useState<User | null>(null)

    /** Ref flag to prevent concurrent refresh calls. */
    const isRefreshingRef = useRef(false)

    const navigate = useNavigate()
    const { setError } = useError()
    const { setSuccess } = useSuccess()

    // ── Keep localStorage in sync whenever tokens change ──────────────────────
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
    }, [tokens])

    // ── Core actions ───────────────────────────────────────────────────────────

    /** Wipe tokens from state and localStorage, then redirect to /login. */
   useEffect(() => {
        const handleForcedLogout = () => {
            setTokens(initialTokens)
            setError("Session expired. Please login again.")
            navigate("/login", { replace: true })
        }

        window.addEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout)
        return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout)
    }, [navigate, setError])

    /** Store new tokens after a successful login. */
    const login = useCallback((response: { access: string; refresh: string }) => {
        setTokens({
            accessToken: response.access,
            refreshToken: response.refresh,
        })
        setSuccess("Successfully logged in")
    }, [setSuccess])

    /**
     * Silently refresh the access token using the stored refresh token.
     */
    const doRefresh = useCallback(async (currentTokens: Tokens) => {
        if (isRefreshingRef.current || !currentTokens.refreshToken) return

        isRefreshingRef.current = true
        setIsRefreshing(true)
        try {
            // Get fresh CSRF token before making the refresh request
            await getFreshCsrfToken()

            const response = await api.post<TokenResponse>(
                "/auth/refresh/",
                { refresh: currentTokens.refreshToken },
            )
            setTokens({
                accessToken: response.data.access,
                refreshToken: response.data.refresh,
            })
            // Silent refresh, no success message needed
        } catch (error) {
            // Refresh failed – force the user back to login.
            setError(error instanceof Error ? error.message : "Session expired. Please login again.")
            setTokens(initialTokens)
            localStorage.setItem(STORAGE_KEY, JSON.stringify(initialTokens))
            navigate("/login", { replace: true })
        } finally {
            isRefreshingRef.current = false
            setIsRefreshing(false)
        }
    }, [navigate, setError])

    // ── Handle tab becoming visible ────────────────────────────────────────────
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== "visible") return

            // Always read the freshest values from localStorage so that
            // changes made in other tabs are picked up.
            const current = loadTokensFromStorage()
            if (!current.refreshToken || !current.accessToken) return

            const now = Date.now()
            const refreshExp = getJwtExpirationMs(current.refreshToken)
            const accessExp = getJwtExpirationMs(current.accessToken)

            if (refreshExp < now) {
                // Refresh token itself is expired – hard logout.
                setError("Session expired. Please login again.")
                //logout in 3 seconds
                setTimeout(() => { logout() }, 3000)
                return
            }

            if (accessExp <= now) {
                // Access token expired but refresh token is still valid.
                doRefresh(current)
            }
        }

        document.addEventListener("visibilitychange", handleVisibilityChange)
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
    }, [doRefresh, setError])

    // ── Fetch the current user profile whenever we have an access token ─────────
    // Runs on first load (rehydrated token) and after each login/refresh so admin
    // status survives a page reload. Failures fall back to a null (non-admin) user.
    useEffect(() => {
        if (!tokens.accessToken) return

        let cancelled = false
        AuthAPI.getMe()
            .then((response) => {
                if (!cancelled) setUser(response.data)
            })
            .catch(() => {
                if (!cancelled) setUser(null)
            })
        return () => { cancelled = true }
    }, [tokens.accessToken])

    // ── Context values ─────────────────────────────────────────────────────────

    const isAuthenticated = useMemo(() => !!tokens.accessToken, [tokens.accessToken])

    // Derived rather than reset via setState: falls back to null the instant the
    // token disappears, without waiting on the effect above to run.
    const currentUser = useMemo(
        () => (tokens.accessToken ? user : null),
        [tokens.accessToken, user],
    )
    const isAdmin = useMemo(() => !!currentUser?.is_staff, [currentUser])

    const stateValue = useMemo(
        () => ({ ...tokens, isAuthenticated, isRefreshing, user: currentUser, isAdmin }),
        [tokens, isAuthenticated, isRefreshing, currentUser, isAdmin],
    )

    const dispatchValue = useMemo(
        () => ({ login, logout:logout }),
        [login],
    )

    return (
        <AuthenticationStateContext.Provider value={stateValue}>
            <AuthenticationDispatchContext.Provider value={dispatchValue}>
                {children}
            </AuthenticationDispatchContext.Provider>
        </AuthenticationStateContext.Provider>
    )
}
